/**
 * Persistent yellow top-banner shown while an admin is impersonating a
 * regular user (see `lib/auth/impersonation.ts` for the boot handshake,
 * and `models/admin/Users.js#putLoginAs` for the backend token mint).
 *
 * Visual contract:
 *   - Sticky to the very top of the viewport, ABOVE the layout's Topbar /
 *     TopNav. Yellow background, high-contrast text.
 *   - Renders nothing when `impersonation.active === false` — every layout
 *     can include this component unconditionally without affecting the
 *     normal user experience.
 *
 * Exit button: calls `useAuth.exitImpersonation()` (clears the session-
 * storage marker, runs `logout()` which revokes the token server-side and
 * drops every TanStack cache entry), then hard-navigates to `/login`. A
 * hard navigation is preferred over `react-router` here so any per-target-
 * user singletons (sockets, in-flight requests, lazy-loaded chunks that
 * captured the old token) are fully torn down.
 */
import { useState } from 'react';

import { useAuth } from '@/pages/auth';

export function ImpersonationBanner() {
  const impersonation = useAuth((s) => s.impersonation);
  const user = useAuth((s) => s.user);
  const exitImpersonation = useAuth((s) => s.exitImpersonation);

  const [exiting, setExiting] = useState(false);

  if (!impersonation.active) return null;

  // Best-effort display name. The user payload may be momentarily empty
  // during the boot handshake (between `impersonation.ts` repacking the
  // token and `useAuth.init()` resolving `auth/verify`); show a neutral
  // "user" label rather than letting the banner blink in with "undefined".
  const firstname = (user?.firstname ?? '').trim();
  const lastname = (user?.lastname ?? '').trim();
  const fullName = [firstname, lastname].filter(Boolean).join(' ');
  const displayName = fullName || user?.username || 'this user';

  async function handleExit() {
    if (exiting) return;
    setExiting(true);
    try {
      await exitImpersonation();
    } finally {
      // Hard navigation — see component-level comment. We DON'T wrap in a
      // try/catch because logout() is best-effort by design (network
      // errors are swallowed in the store), and at this point we've
      // already cleared the local marker either way.
      window.location.href = '/login';
    }
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="impersonation-banner"
      className="sticky top-0 z-[60] w-full border-b border-yellow-500 bg-yellow-400 text-yellow-950 shadow-sm"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 text-sm font-medium sm:px-6">
        <span className="min-w-0 truncate">
          <span aria-hidden="true" className="mr-2">⚠</span>
          Impersonating <span className="font-semibold">{displayName}</span>
        </span>
        <button
          type="button"
          onClick={handleExit}
          disabled={exiting}
          className="shrink-0 rounded border border-yellow-700 bg-yellow-300 px-3 py-1 text-xs font-semibold text-yellow-950 transition-colors hover:bg-yellow-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-700 disabled:opacity-60"
        >
          {exiting ? 'Exiting…' : 'Exit impersonation'}
        </button>
      </div>
    </div>
  );
}
